import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardBody,
  Spinner,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@nextui-org/react';
import {
  CalendarDays,
  CalendarRange,
  List as ListIcon,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  ExternalLink,
  Trophy,
  Tag,
  AlertTriangle,
} from 'lucide-react';
import calendarService, {
  CalendarEvent,
  CalendarSource,
} from '../services/calendarService';

const TEAL = '#3FCED2';

type ViewMode = 'calendar' | 'list';

const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const WEEKDAY_SHORT_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthYear(d: Date): string {
  return `${MONTH_NAMES_DE[d.getMonth()]} ${d.getFullYear()}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

interface ExpandedEvent {
  event: CalendarEvent;
  displayDate: Date;
}

const CalendarPage: React.FC = () => {
  const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]);
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [currentMonth, setCurrentMonth] = useState<Date>(startOfDay(new Date()));
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<CalendarEvent[]>([]);

  const [pendingEventUrl, setPendingEventUrl] = useState<CalendarEvent | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // ---------- Initial Load ----------
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [events, srcs] = await Promise.all([
        calendarService.getUpcomingEvents(500, 0),
        calendarService.getAllSources(),
      ]);
      setAllEvents(events);
      setSources(srcs);
    } catch (err) {
      console.error('Fehler beim Laden des Kalenders:', err);
      setError('Kalender konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---------- Filtered Events ----------
  const filteredEvents = useMemo(() => {
    if (!selectedOrg) return allEvents;
    return allEvents.filter((e) => e.organization === selectedOrg);
  }, [allEvents, selectedOrg]);

  // ---------- Available Organizations ----------
  const organizations = useMemo(() => {
    const set = new Set<string>();
    allEvents.forEach((e) => {
      if (e.organization) set.add(e.organization);
    });
    return Array.from(set).sort();
  }, [allEvents]);

  // ---------- Search ----------
  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      setSearchResults([]);
      return;
    }
    const lower = searchTerm.toLowerCase();
    const results = allEvents
      .filter((e) => {
        const title = (e.title || '').toLowerCase();
        const location = calendarService.formatLocation(e).toLowerCase();
        const org = (e.organization || '').toLowerCase();
        return title.includes(lower) || location.includes(lower) || org.includes(lower);
      })
      .sort((a, b) => (a.startDate || 0) - (b.startDate || 0))
      .slice(0, 10);
    setSearchResults(results);
  }, [searchTerm, allEvents]);

  // ---------- Events for selected day ----------
  const eventsOnSelectedDay = useMemo(() => {
    const dayStart = startOfDay(selectedDate).getTime();
    const dayEnd = dayStart + 24 * 60 * 60 * 1000 - 1;
    return filteredEvents.filter((e) => {
      if (!e.startDate) return false;
      const start = e.startDate;
      const end = e.endDate || e.startDate;
      return start <= dayEnd && end >= dayStart;
    });
  }, [filteredEvents, selectedDate]);

  // ---------- Calendar grid days ----------
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days: Array<{ date: Date | null; inMonth: boolean }> = [];
    for (let i = 0; i < firstWeekday; i++) days.push({ date: null, inMonth: false });
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ date: new Date(year, month, d), inMonth: true });
    }
    while (days.length % 7 !== 0) days.push({ date: null, inMonth: false });
    return days;
  }, [currentMonth]);

  const eventCountByDay = useMemo(() => {
    const map = new Map<string, number>();
    filteredEvents.forEach((e) => {
      if (!e.startDate) return;
      const start = startOfDay(new Date(e.startDate));
      const end = startOfDay(new Date(e.endDate || e.startDate));
      const d = new Date(start);
      while (d.getTime() <= end.getTime()) {
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        map.set(key, (map.get(key) || 0) + 1);
        d.setDate(d.getDate() + 1);
      }
    });
    return map;
  }, [filteredEvents]);

  // ---------- List view: expanded events grouped by month ----------
  const expandedByMonth = useMemo(() => {
    const todayStart = startOfDay(new Date());
    const expanded: ExpandedEvent[] = [];
    filteredEvents.forEach((event) => {
      if (!event.startDate) return;
      const start = startOfDay(new Date(event.startDate));
      const end = startOfDay(new Date(event.endDate || event.startDate));
      const d = new Date(start);
      while (d.getTime() <= end.getTime()) {
        if (d.getTime() >= todayStart.getTime()) {
          expanded.push({ event, displayDate: new Date(d) });
        }
        d.setDate(d.getDate() + 1);
      }
    });
    expanded.sort((a, b) => a.displayDate.getTime() - b.displayDate.getTime());

    const grouped: Record<string, ExpandedEvent[]> = {};
    expanded.forEach((e) => {
      const key = monthKey(e.displayDate);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(e);
    });
    return { expanded, grouped };
  }, [filteredEvents]);

  // ---------- Open Event URL with Modal ----------
  const openEventUrl = (event: CalendarEvent) => {
    if (!event.sourceUrl) return;
    setPendingEventUrl(event);
    onOpen();
  };

  const confirmOpenEvent = () => {
    if (pendingEventUrl?.sourceUrl) {
      window.open(pendingEventUrl.sourceUrl, '_blank', 'noopener,noreferrer');
    }
    onClose();
    setPendingEventUrl(null);
  };

  const toggleMonthCollapsed = (key: string) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ---------- States ----------
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Turnier-Kalender | DartCloud</title>
        </Helmet>
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" style={{ color: TEAL }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Turnier-Kalender | DartCloud</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <AlertTriangle size={64} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 mb-4">{error}</p>
          <Button
            onPress={fetchData}
            style={{ backgroundColor: TEAL, color: '#000' }}
          >
            Erneut versuchen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>Turnier-Kalender | DartCloud</title>
        <meta
          name="description"
          content="Turnierkalender von Verbänden: PDC, WDF, DDV und mehr. Alle kommenden Dart-Turniere auf einen Blick."
        />
        <link rel="canonical" href="https://dartcloudapp.de/kalender" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <CalendarDays size={40} style={{ color: TEAL }} />
              Turnier-Kalender
            </h1>
            <p className="text-gray-400">Turnierkalender von Verbänden</p>
          </div>
          <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main area */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            {viewMode === 'calendar' ? (
              <>
                <CalendarMonth
                  currentMonth={currentMonth}
                  selectedDate={selectedDate}
                  calendarDays={calendarDays}
                  eventCountByDay={eventCountByDay}
                  onSelectDate={setSelectedDate}
                  onChangeMonth={(delta) => {
                    const next = new Date(currentMonth);
                    next.setMonth(next.getMonth() + delta);
                    setCurrentMonth(next);
                  }}
                />
                <div className="mt-6">
                  <EventsForDay
                    date={selectedDate}
                    events={eventsOnSelectedDay}
                    onOpenEvent={openEventUrl}
                  />
                </div>
              </>
            ) : (
              <FullEventsList
                grouped={expandedByMonth.grouped}
                totalCount={expandedByMonth.expanded.length}
                collapsedMonths={collapsedMonths}
                onToggleMonth={toggleMonthCollapsed}
                onOpenEvent={openEventUrl}
              />
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 order-1 lg:order-2 space-y-4">
            <SearchSection
              searchTerm={searchTerm}
              onChange={setSearchTerm}
              results={searchResults}
              onOpenEvent={openEventUrl}
            />
            <FilterSection
              organizations={organizations}
              selectedOrg={selectedOrg}
              onChange={setSelectedOrg}
            />
            <SourcesList sources={sources} />
          </div>
        </div>
      </div>

      {/* External Link Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        backdrop="blur"
        classNames={{ base: 'bg-zinc-900', header: 'border-b border-zinc-800' }}
      >
        <ModalContent>
          <ModalHeader className="flex items-center gap-2">
            <CalendarDays size={24} className="text-gray-400" />
            <span className="truncate">{pendingEventUrl?.title || 'Event'}</span>
          </ModalHeader>
          <ModalBody>
            <p className="text-gray-400 font-medium">
              {pendingEventUrl ? calendarService.formatDateRange(pendingEventUrl) : ''}
            </p>
            <p className="text-gray-300">Du wirst zur Quelle dieses Events weitergeleitet.</p>
            <div className="bg-zinc-800 rounded-lg p-3 text-xs text-gray-400 break-all">
              {pendingEventUrl?.sourceUrl}
            </div>
            <p className="text-xs text-gray-500 italic">
              Wir übernehmen keine Verantwortung für den Inhalt externer Webseiten.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Abbrechen
            </Button>
            <Button
              onPress={confirmOpenEvent}
              startContent={<ExternalLink size={16} />}
              style={{ backgroundColor: TEAL, color: '#000' }}
            >
              Link öffnen
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

// ============= View Mode Toggle =============
const ViewModeToggle: React.FC<{ viewMode: ViewMode; onChange: (m: ViewMode) => void }> = ({
  viewMode,
  onChange,
}) => {
  return (
    <div className="inline-flex bg-zinc-800 rounded-lg p-1">
      <button
        onClick={() => onChange('calendar')}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
          viewMode === 'calendar' ? 'text-black' : 'text-gray-400 hover:text-gray-300'
        }`}
        style={{ backgroundColor: viewMode === 'calendar' ? TEAL : 'transparent' }}
      >
        <CalendarRange size={16} />
        Kalender
      </button>
      <button
        onClick={() => onChange('list')}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
          viewMode === 'list' ? 'text-black' : 'text-gray-400 hover:text-gray-300'
        }`}
        style={{ backgroundColor: viewMode === 'list' ? TEAL : 'transparent' }}
      >
        <ListIcon size={16} />
        Liste
      </button>
    </div>
  );
};

// ============= Calendar Month Grid =============
interface CalendarMonthProps {
  currentMonth: Date;
  selectedDate: Date;
  calendarDays: Array<{ date: Date | null; inMonth: boolean }>;
  eventCountByDay: Map<string, number>;
  onSelectDate: (d: Date) => void;
  onChangeMonth: (delta: number) => void;
}

const CalendarMonth: React.FC<CalendarMonthProps> = ({
  currentMonth,
  selectedDate,
  calendarDays,
  eventCountByDay,
  onSelectDate,
  onChangeMonth,
}) => {
  const today = startOfDay(new Date());

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardBody className="p-4 md:p-6">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <Button isIconOnly variant="light" onPress={() => onChangeMonth(-1)}>
            <ChevronLeft size={20} />
          </Button>
          <h2 className="text-lg md:text-xl font-bold">{formatMonthYear(currentMonth)}</h2>
          <Button isIconOnly variant="light" onPress={() => onChangeMonth(1)}>
            <ChevronRight size={20} />
          </Button>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAY_SHORT_DE.map((w) => (
            <div
              key={w}
              className="text-center text-xs uppercase tracking-wide text-gray-500 font-semibold py-1"
            >
              {w}
            </div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((cell, idx) => {
            if (!cell.date) {
              return <div key={idx} className="h-12 md:h-16" />;
            }
            const date = cell.date;
            const isSelected = isSameDay(date, selectedDate);
            const isToday = isSameDay(date, today);
            const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
            const count = eventCountByDay.get(key) || 0;

            return (
              <button
                key={idx}
                onClick={() => onSelectDate(date)}
                className={`relative h-12 md:h-16 rounded-lg flex flex-col items-center justify-center transition-colors ${
                  isSelected
                    ? 'text-black font-bold'
                    : count > 0
                      ? 'bg-zinc-800 hover:bg-zinc-700 text-white'
                      : 'hover:bg-zinc-800 text-gray-300'
                }`}
                style={{ backgroundColor: isSelected ? TEAL : undefined }}
              >
                <span className={`text-sm md:text-base ${isToday && !isSelected ? 'font-bold' : ''}`}
                  style={{ color: isToday && !isSelected ? TEAL : undefined }}
                >
                  {date.getDate()}
                </span>
                {count > 0 && (
                  <span
                    className="absolute bottom-1 text-[10px] font-semibold"
                    style={{ color: isSelected ? '#000' : TEAL }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
};

// ============= Events for selected day =============
interface EventsForDayProps {
  date: Date;
  events: CalendarEvent[];
  onOpenEvent: (e: CalendarEvent) => void;
}

const EventsForDay: React.FC<EventsForDayProps> = ({ date, events, onOpenEvent }) => {
  const dateLabel = date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardBody className="p-0">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-semibold">{dateLabel}</h3>
          <Chip
            size="sm"
            variant="flat"
            style={{ backgroundColor: `${TEAL}33`, color: TEAL }}
          >
            {events.length} {events.length === 1 ? 'Event' : 'Events'}
          </Chip>
        </div>
        {events.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <CalendarDays size={48} className="mx-auto text-gray-600 mb-3" />
            <p className="text-gray-400">Keine Events an diesem Tag</p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {events.map((e) => (
              <li key={e.id || e._id}>
                <EventRow event={e} onOpen={() => onOpenEvent(e)} />
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};

// ============= Single Event Row =============
const EventRow: React.FC<{ event: CalendarEvent; onOpen: () => void; displayDate?: Date }> = ({
  event,
  onOpen,
  displayDate,
}) => {
  const location = calendarService.formatLocation(event);
  const prize = event.prizeMoneyFormatted
    || (event.prizeMoney != null
      ? `${event.prizeMoneyUnit || ''}${event.prizeMoney.toLocaleString('de-DE')}`
      : null);

  return (
    <button
      onClick={onOpen}
      disabled={!event.sourceUrl}
      className="w-full text-left px-4 py-4 hover:bg-zinc-800/50 transition-colors disabled:cursor-default"
    >
      <div className="flex items-start gap-3">
        {displayDate && (
          <div
            className="flex-shrink-0 w-12 text-center rounded-lg py-1.5 px-1"
            style={{ backgroundColor: `${TEAL}1A` }}
          >
            <div className="text-xs uppercase font-semibold" style={{ color: TEAL }}>
              {displayDate.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '')}
            </div>
            <div className="text-lg font-bold" style={{ color: TEAL }}>
              {displayDate.getDate()}
            </div>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="font-semibold text-white line-clamp-2 flex-1">
              {event.title || 'Event'}
            </h4>
            {event.sourceUrl && (
              <ExternalLink size={14} className="text-gray-500 flex-shrink-0 mt-1" />
            )}
          </div>
          {event.organization && (
            <p className="text-xs text-gray-500 mb-1">{event.organization}</p>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
            {!displayDate && (
              <span className="flex items-center gap-1">
                <CalendarDays size={12} />
                {calendarService.formatDateRange(event)}
              </span>
            )}
            {location !== '-' && (
              <span className="flex items-center gap-1">
                <MapPin size={12} />
                <span className="line-clamp-1">{location}</span>
              </span>
            )}
            {prize && (
              <span className="flex items-center gap-1" style={{ color: TEAL }}>
                <Trophy size={12} />
                {prize}
              </span>
            )}
          </div>
          {event.tags && event.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {event.tags.slice(0, 3).map((t, i) => (
                <Chip
                  key={i}
                  size="sm"
                  variant="flat"
                  className="bg-zinc-800 text-gray-400 text-[10px]"
                  startContent={<Tag size={10} />}
                >
                  {t}
                </Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// ============= Full Events List (List view) =============
interface FullEventsListProps {
  grouped: Record<string, ExpandedEvent[]>;
  totalCount: number;
  collapsedMonths: Set<string>;
  onToggleMonth: (key: string) => void;
  onOpenEvent: (e: CalendarEvent) => void;
}

const FullEventsList: React.FC<FullEventsListProps> = ({
  grouped,
  totalCount,
  collapsedMonths,
  onToggleMonth,
  onOpenEvent,
}) => {
  const sortedKeys = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  if (totalCount === 0) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardBody className="p-12 text-center">
          <CalendarDays size={64} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-lg font-semibold text-gray-400 mb-1">
            Keine Events gefunden
          </h3>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardBody className="p-0">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <ListIcon size={18} style={{ color: TEAL }} />
            <h3 className="font-bold">Alle Events</h3>
          </div>
          <Chip size="sm" variant="flat" style={{ backgroundColor: `${TEAL}33`, color: TEAL }}>
            {totalCount} Events
          </Chip>
        </div>
        {sortedKeys.map((key) => {
          const events = grouped[key];
          const [yearStr, monthStr] = key.split('-');
          const monthDate = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, 1);
          const isCollapsed = collapsedMonths.has(key);

          return (
            <div key={key}>
              <button
                onClick={() => onToggleMonth(key)}
                className="w-full flex items-center gap-2 px-5 py-3 bg-zinc-850/50 hover:bg-zinc-800/60 border-b border-zinc-800 transition-colors text-left"
                style={{ backgroundColor: '#18181b' }}
              >
                {isCollapsed ? (
                  <ChevronRight size={18} className="text-gray-400" />
                ) : (
                  <ChevronDown size={18} className="text-gray-400" />
                )}
                <span className="font-bold">{formatMonthYear(monthDate)}</span>
                <Chip size="sm" className="bg-zinc-700 text-gray-300">
                  {events.length}
                </Chip>
                <div className="ml-auto">
                  {isCollapsed ? (
                    <ChevronDown size={16} className="text-gray-500" />
                  ) : (
                    <ChevronUp size={16} className="text-gray-500" />
                  )}
                </div>
              </button>
              {!isCollapsed && (
                <ul className="divide-y divide-zinc-800">
                  {events.map((e, i) => (
                    <li key={`${e.event.id || e.event._id}-${i}`}>
                      <EventRow
                        event={e.event}
                        displayDate={e.displayDate}
                        onOpen={() => onOpenEvent(e.event)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
};

// ============= Search Section =============
interface SearchSectionProps {
  searchTerm: string;
  onChange: (v: string) => void;
  results: CalendarEvent[];
  onOpenEvent: (e: CalendarEvent) => void;
}

const SearchSection: React.FC<SearchSectionProps> = ({ searchTerm, onChange, results, onOpenEvent }) => (
  <Card className="bg-zinc-900 border-zinc-800">
    <CardBody className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Search size={18} style={{ color: TEAL }} />
        <h3 className="font-bold">Suche</h3>
      </div>
      <Input
        placeholder="Events suchen..."
        value={searchTerm}
        onChange={(e) => onChange(e.target.value)}
        startContent={<Search size={16} className="text-gray-400" />}
        classNames={{
          input: 'bg-zinc-800',
          inputWrapper: 'bg-zinc-800 border-zinc-700',
        }}
      />
      {results.length > 0 && (
        <ul className="mt-3 divide-y divide-zinc-800 max-h-80 overflow-y-auto">
          {results.map((e) => (
            <li key={e.id || e._id}>
              <button
                onClick={() => onOpenEvent(e)}
                className="w-full text-left py-2 hover:bg-zinc-800/50 px-2 rounded transition-colors"
              >
                <p className="text-sm font-medium text-white line-clamp-1">
                  {e.title || 'Event'}
                </p>
                <p className="text-xs text-gray-500 line-clamp-1">
                  {calendarService.formatDateRange(e)} · {calendarService.formatLocation(e)}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </CardBody>
  </Card>
);

// ============= Filter Section =============
interface FilterSectionProps {
  organizations: string[];
  selectedOrg: string | null;
  onChange: (org: string | null) => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({ organizations, selectedOrg, onChange }) => (
  <Card className="bg-zinc-900 border-zinc-800">
    <CardBody className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Filter size={18} style={{ color: TEAL }} />
        <h3 className="font-bold">Filter</h3>
      </div>
      <Select
        label="Organisation"
        placeholder="Alle Organisationen"
        selectedKeys={selectedOrg ? [selectedOrg] : []}
        onChange={(e) => onChange(e.target.value || null)}
        classNames={{
          trigger: 'bg-zinc-800 border-zinc-700',
          listbox: 'bg-zinc-900',
        }}
      >
        {organizations.map((org) => (
          <SelectItem key={org} value={org}>
            {org}
          </SelectItem>
        ))}
      </Select>
      {selectedOrg && (
        <Button
          size="sm"
          variant="light"
          className="mt-2 text-gray-400"
          onPress={() => onChange(null)}
        >
          Filter zurücksetzen
        </Button>
      )}
    </CardBody>
  </Card>
);

// ============= Sources List =============
const SourcesList: React.FC<{ sources: CalendarSource[] }> = ({ sources }) => {
  if (sources.length === 0) return null;
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardBody className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <CalendarRange size={18} style={{ color: TEAL }} />
          <h3 className="font-bold">Quellen</h3>
        </div>
        <ul className="space-y-2">
          {sources.map((s) => (
            <li
              key={s.id || s._id}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-gray-300 line-clamp-1">
                {s.name || s.shortName || 'Quelle'}
              </span>
              {typeof s.eventCount === 'number' && (
                <span className="text-xs text-gray-500">{s.eventCount}</span>
              )}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
};

export default CalendarPage;
